// Palefò, through our own server. The browser only ever calls /api/* — the
// API key is added there (see api/), so it never reaches this bundle.

const CLIPS_KEPT = 30;
const clips = new Map();   // request → Promise<ArrayBuffer>; replaying an answer is never billed twice

async function failure(res, what) {
  const body = await res.json().catch(() => ({}));
  const e = new Error(body.error || `${what}_${res.status}`);
  e.status = res.status;
  return e;
}

const post = (path, body) => fetch(path, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

/**
 * Is the voice configured, and who can speak?
 * { ready, voices, speakers }, each a list of { id, name, gender }: `voices`
 * speak Kreyòl, `speakers` the other languages.
 */
export async function voiceStatus() {
  const list = (v) => (Array.isArray(v) ? v : []);
  try {
    const res = await fetch('/api/voice');
    const body = res.ok ? await res.json() : {};
    return { ready: !!body.ready, voices: list(body.voices), speakers: list(body.speakers) };
  } catch {
    return { ready: false, voices: [], speakers: [] };
  }
}

/** Speech in `lang` → text. Resolves { transcription }, or { noSpeech: true } when the clip was silence. */
export async function transcribe({ audioBase64, format }, lang) {
  const res = await post('/api/asr', { audioBase64, format, lang });
  if (!res.ok) throw await failure(res, 'asr');
  return res.json();
}

/**
 * An answer → the bytes of a spoken clip. `answer` is { source, targets } in
 * figures; the server composes the sentence in `lang` itself, so it only ever
 * says a conversion.
 */
export function synthesize(answer, { lang, voice }) {
  return clip(answer.switched ? { lang, voice, switched: answer.switched } : { lang, voice, source: answer.source, targets: answer.targets });
}

/** The BRH's reference rate, spoken in Kreyòl by `voice` (a Kreyòl voice id). */
export function synthesizeRate(rate, voice) {
  return clip({ lang: 'ht', voice, rate });
}

/** A spoken clip for a request /api/tts composes itself; the same request is never billed twice. */
function clip(request) {
  const key = JSON.stringify(request);
  if (clips.has(key)) return clips.get(key);
  const bytes = post('/api/tts', request).then(async (res) => {
    if (!res.ok) throw await failure(res, 'tts');
    return res.arrayBuffer();
  });
  clips.set(key, bytes);
  bytes.catch(() => clips.delete(key));
  if (clips.size > CLIPS_KEPT) clips.delete(clips.keys().next().value);
  return bytes;
}

// The languages a question can be asked in. Palefò's Assistant takes only what
// its gateway lets a caller declare (api/_lib/palefo.js) — not Russian,
// Chinese, Japanese or Korean.
export const ASSISTANT_LANGUAGES = new Set(['ht', 'en', 'fr', 'es', 'pt', 'it', 'de']);

/**
 * A question about the exchange → the Assistant's answer, { text, audio }.
 * `audio` is the spoken reply as bytes, or null. The answer is in Kreyòl
 * whatever language the question was in. `rate` and `source` are the figure on
 * screen and where it came from, so the answer agrees with the page.
 */
export async function ask({ question, lang, voice, rate, source }) {
  const res = await post('/api/ask', { question, lang, voice, rate, source });
  if (!res.ok) throw await failure(res, 'ask');
  const { text, audioBase64 } = await res.json();
  const audio = audioBase64 ? Uint8Array.from(atob(audioBase64), (c) => c.charCodeAt(0)).buffer : null;
  return { text, audio };
}

/**
 * The rates the Haitian banks post for the dollar, as the BRH publishes them
 * and as our server last read them: { date, reference, banks, highest, lowest, at }.
 */
export async function banks() {
  const res = await fetch('/api/banks');
  if (!res.ok) throw await failure(res, 'banks');
  return res.json();
}
